import React from 'react';
import { getPaymentIntent, PaymentIntentError } from '../data/payment/getPaymentIntent';

/** After this long without an answer, the page tells the patient it can take a while. */
export const PAYMENT_INTENT_SLOW_MS = 8_000;

export type PaymentIntentStatus = 'idle' | 'loading' | 'slow' | 'ready' | 'error';

interface PaymentIntentState {
  status: PaymentIntentStatus;
  clientSecret: string | null;
  error: PaymentIntentError | null;
}

const IDLE: PaymentIntentState = { status: 'idle', clientSecret: null, error: null };

interface UsePaymentIntentArgs {
  /** True once the patient has clicked Pay Now. */
  enabled: boolean;
  amountCents: number;
  /** Read when the request is made — deliberately NOT a dependency (see below). */
  receiptEmail?: string;
}

/**
 * Owns the self-pay page's PaymentIntent request (CU-17tm466h6uv). Replaces an
 * inline effect that had no error state: a failure left the page on
 * "Connecting to Payment Provider..." forever, and it silently never fired at
 * all when the patient had no contactEmail.
 *
 * - `slow` after 8 s so the patient knows to wait rather than leave
 * - `error` with a typed PaymentIntentError, and `retry()` to try again
 * - a response for a superseded request (amount changed, Pay Now withdrawn,
 *   unmount) is IGNORED — only the latest request may set the client secret
 *
 * ⚠️ receiptEmail is read through a ref, NOT listed as a dependency. If the
 * patient document arrived after the click, a dependency would fire a SECOND
 * PaymentIntent and swap the client secret under a mounted <Elements>, which
 * cannot change its secret after mount.
 */
export function usePaymentIntent({ enabled, amountCents, receiptEmail }: UsePaymentIntentArgs) {
  const [state, setState] = React.useState<PaymentIntentState>(IDLE);
  const [attempt, setAttempt] = React.useState(0);
  const receiptEmailRef = React.useRef(receiptEmail);
  // Declared BEFORE the request effect: effects run in order, so a commit that
  // both updates the email and enables the request sends the fresh value.
  React.useEffect(() => {
    receiptEmailRef.current = receiptEmail;
  }, [receiptEmail]);

  React.useEffect(() => {
    if (!enabled) {
      setState(IDLE);
      return;
    }

    let superseded = false;
    setState({ status: 'loading', clientSecret: null, error: null });
    const slowTimer = setTimeout(() => {
      if (superseded) return;
      setState((current) => (current.status === 'loading' ? { ...current, status: 'slow' } : current));
    }, PAYMENT_INTENT_SLOW_MS);

    getPaymentIntent(amountCents, receiptEmailRef.current)
      .then((clientSecret) => {
        if (superseded) return;
        setState({ status: 'ready', clientSecret, error: null });
      })
      .catch((err: unknown) => {
        if (superseded) return;
        const error =
          err instanceof PaymentIntentError
            ? err
            : new PaymentIntentError('network', (err as Error)?.message ?? String(err));
        setState({ status: 'error', clientSecret: null, error });
      })
      .finally(() => clearTimeout(slowTimer));

    return () => {
      superseded = true;
      clearTimeout(slowTimer);
    };
  }, [enabled, amountCents, attempt]);

  const retry = React.useCallback(() => setAttempt((n) => n + 1), []);

  return { ...state, retry };
}
